<template>
  <div class="route-shell-host relative-position window-height window-width overflow-hidden">
    <router-view v-slot="{ Component, route }">
      <Transition name="route-shell" mode="out-in">
        <component
          :is="Component"
          :key="routeShellKey(route)"
          class="route-shell-view window-height window-width overflow-hidden"
        />
      </Transition>
    </router-view>
  </div>
</template>
<style>
.route-shell-host {
  min-height: 100vh;
  min-height: 100dvh;
  /* Match body page color so route transitions never flash a mismatched base. */
  background: var(--q-light, #fafafa);
}

body.body--dark .route-shell-host {
  background: var(--q-dark, #09090b);
}

.route-shell-enter-active,
.route-shell-leave-active {
  transition:
    opacity 0.78s cubic-bezier(0.22, 1, 0.36, 1),
    transform 0.78s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.68s cubic-bezier(0.22, 1, 0.36, 1);
}

.route-shell-enter-from,
.route-shell-leave-to {
  opacity: 0;
  transform: translateY(8px);
  filter: blur(3px);
}

.route-shell-enter-to,
.route-shell-leave-from {
  filter: blur(0);
}

@media (prefers-reduced-motion: reduce) {
  .route-shell-enter-active,
  .route-shell-leave-active {
    transition-duration: 0.01ms !important;
  }

  .route-shell-enter-from,
  .route-shell-leave-to {
    transform: none;
    filter: none;
  }
}

.no-decoration {
  text-decoration: none;
  color: inherit;
}
/* Little modification to make sure the password field take all width available in all application */
.Password {
  max-width: inherit !important;
}
.carriage-return {
  white-space: pre-line;
}
</style>
<script setup>
import { provide, ref, onBeforeUnmount, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import { usePreferencesStore } from 'src/stores/usePreferencesStore';
import { setDarkMode } from 'src/boot/prefs';
import { reqGetCurrentBuild } from 'src/api/app';
import { reqGetProcessingTimeSummary } from 'src/api/declaration';
import { clearCachesAndReload } from 'src/utils/browser';
import { useNomenclatureStore } from './stores/useNomenclatureStore';
import { useNotificationsStore } from './stores/useNotificationsStore';
import { useBannerStore } from 'src/stores/useBannerStore';
import { useProcessingTimeStore } from 'src/stores/useProcessingTimeStore';
import { createAppWebSocket } from 'services/websocket';

import { useProfileStore } from 'src/stores/useProfileStore';
import { loadAndApplyTheme, saveSelectedThemeId, getSelectedThemeId } from 'src/services/theme';
import { applyFont, saveSelectedFontId, getSelectedFontId } from 'src/services/font';
import 'services/formatters';

import { Role } from 'src/constants/app';
import { NotifTypes } from 'src/constants/notification';
import { displayInfoMessage } from 'src/composables/useNotify';

/** Top-level layout only — avoids re-animating MainLayout on in-app child route changes. */
function routeShellKey(route) {
  const top = route.matched[0];
  return String(top?.name ?? top?.path ?? route.path);
}

if (import.meta.env.AGGRID_LICENSE_KEY) {
  ModuleRegistry.registerModules([AllEnterpriseModule]);
  LicenseManager.setLicenseKey(import.meta.env.AGGRID_LICENSE_KEY);
}

const { locale, t } = useI18n();
const preferences = usePreferencesStore();
const nomenclatureStore = useNomenclatureStore();
const notificationsStore = useNotificationsStore();
const { unreadCount } = storeToRefs(notificationsStore);
const bannerStore = useBannerStore();
const processingTimeStore = useProcessingTimeStore();
const { role: userRole, initialized: profileInitialized } = storeToRefs(useProfileStore());
const notifPopUp = ref({
  show: false,
  last_msg: {}
});

let updateCheckInterval;
let initialBuild = null;

const wsDeclarationData = ref({});
provide('wsDeclarationData', wsDeclarationData);
provide('notifPopUp', notifPopUp.value);
provide('nbNotifs', unreadCount);

function showUpdateNotification(t) {
  displayInfoMessage(t('new_app_version_available'), {
    badgeStyle: 'display: None',
    timeout: 0,
    actions: [
      {
        label: t('reload'),
        color: 'white',
        icon: 'refresh',
        handler: async () => {
          try {
            await clearCachesAndReload();
          } catch (error) {
            console.error('Error while reloading the app', error);
          }
        }
      }
    ]
  });
}

async function checkForUpdates() {
  try {
    if (!initialBuild) initialBuild = localStorage.getItem('initialBuild');
    const currentBuild = await reqGetCurrentBuild();
    if (!!currentBuild && currentBuild !== initialBuild) {
      showUpdateNotification(t);
    }
  } catch (error) {
    console.error('Error while checking for updates', error);
  }
}

function initCheckForUpdates() {
  if (import.meta.env.DEV) return;
  reqGetCurrentBuild()
    .then((build) => {
      if (!build) throw new Error('No build number found');
      initialBuild = build;
      localStorage.setItem('initialBuild', initialBuild);
      updateCheckInterval = window.setInterval(async () => {
        await checkForUpdates();
      }, 60000);
    })
    .catch((error) => {
      console.error('Error while checking for updates', error);
      setTimeout(initCheckForUpdates, 30000);
    });
}

// Clean up the polling when the component unmounts
onBeforeUnmount(() => {
  clearInterval(updateCheckInterval);
});

// Function to update theme and colors
async function updateTheme() {
  // Ensure theme is loaded from localStorage if not set
  if (!preferences.interface.theme || preferences.interface.theme.trim() === '') {
    preferences.interface.theme = getSelectedThemeId();
  }
  const themeId = preferences.interface.theme || 'default';
  await loadAndApplyTheme(themeId, preferences.isDarkMode);
  setDarkMode();
}

// Function to apply selected font
function updateFont() {
  if (!preferences.interface.font) {
    preferences.interface.font = getSelectedFontId();
  }
  applyFont(preferences.interface.font);
}

// Initialize theme and font on app load
updateTheme();
updateFont();

// Watch for theme and dark mode changes
watch(
  () => [preferences.interface.theme, preferences.isDarkMode],
  async () => {
    await updateTheme();
  },
  { immediate: false }
);

// Watch for font changes
watch(
  () => preferences.interface.font,
  () => {
    updateFont();
  },
  { immediate: false }
);

preferences.$subscribe(async () => {
  // Update theme when preferences change
  await updateTheme();

  if (locale.value !== preferences.interface.lang) {
    locale.value = preferences.interface.lang;
    nomenclatureStore.clearNomenclature();
    await nomenclatureStore.requestNomenc();
  }

  // Save theme to localStorage when it changes
  if (preferences.interface.theme) {
    saveSelectedThemeId(preferences.interface.theme);
  }
  // Save font to localStorage when it changes
  if (preferences.interface.font) {
    saveSelectedFontId(preferences.interface.font);
  }

  await preferences.savePreferences();
});

async function loadInitialProcessingTimeSummary() {
  try {
    const summaryData = await reqGetProcessingTimeSummary();
    if (summaryData && typeof summaryData === 'object') {
      processingTimeStore.updateProcessingTime({
        rita: summaryData.rita ?? null,
        deltaI: summaryData.deltaI ?? null,
        deltaE: summaryData.deltaE ?? null,
        deltaT: summaryData.deltaT ?? null,
        deltaI15minP50: summaryData.deltaI15minP50 ?? null,
        deltaE15minP50: summaryData.deltaE15minP50 ?? null,
        deltaT15minP50: summaryData.deltaT15minP50 ?? null
      });
    }
  } catch (error) {
    console.error('Error while loading initial processing time summary', error);
  }
}

// WebSocket stuff
async function handleMessage(event) {
  const payload = JSON.parse(event.data);
  if (payload.nb_new_msgs !== undefined) {
    notificationsStore.setUnreadCount(payload.nb_new_msgs);
  }

  if (!payload.last_msg?.type) {
    return;
  }

  if (payload.last_msg.type === NotifTypes.BANNER) {
    const bannerUpdate = payload.last_msg.message?.[0];
    if (bannerUpdate) {
      bannerStore.handleWebSocketUpdate(bannerUpdate);
    }
    return;
  }

  notifPopUp.value.show = true;
  notifPopUp.value.last_msg = payload.last_msg;
  if (payload.last_msg.type === NotifTypes.NEW_DECLARATION_STATUS) {
    notifPopUp.value.show = false;
    wsDeclarationData.value.last_msg = payload.last_msg;
    return;
  }
  if (payload.last_msg.type === NotifTypes.PROCESSING_TIME) {
    notifPopUp.value.show = false;
    if (userRole.value !== Role.Maintainer) {
      return;
    }
    const processingTimePayload = payload.last_msg.message?.[0] || null;
    processingTimeStore.updateProcessingTime(processingTimePayload);
    wsDeclarationData.value.processing_time = processingTimePayload;
    return;
  }
  setTimeout(() => {
    notifPopUp.value.show = false;
  }, 5000);
}

createAppWebSocket({
  onMessage: handleMessage,
  onError: (event) => {
    console.error('WebSocket Error: ', event);
  },
  onClose: (event) => {
    if (event.wasClean) {
      console.error(`Closed cleanly, code=${event.code}, reason=${event.reason}`);
    } else {
      console.error('Connection died');
    }
  }
});
let processingTimeSummaryLoaded = false;

watch(
  [userRole, profileInitialized],
  ([role, initialized]) => {
    if (initialized && role === Role.Maintainer && !processingTimeSummaryLoaded) {
      processingTimeSummaryLoaded = true;
      loadInitialProcessingTimeSummary();
    }
  },
  { immediate: true }
);

initCheckForUpdates();
</script>
